import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { 
  Package, Truck, CheckCircle, Clock, MapPin, 
  ChevronLeft, ShoppingBag, User, AlertCircle, Store, MessageCircle
} from 'lucide-react';
import { getSaleItemDisplayName } from '../utils/productLabels';
import toast from 'react-hot-toast';
import PushAvisoPedido from '../components/ui/PushAvisoPedido';

export default function Rastreo() {
  const { trackingCode } = useParams();
  const [searchParams] = useSearchParams();
  const isNewOrder = searchParams.get('new') === 'true';

  // Mensaje de retorno desde el checkout externo de Getnet (?payment=...).
  // El estado real del pago lo confirma el webhook; esto es solo feedback al cliente.
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (!payment) return;
    if (payment === 'success' || payment === 'approved') {
      toast.success('¡Pago confirmado! Estamos procesando tu pedido.');
    } else if (payment === 'failure' || payment === 'error' || payment === 'rejected') {
      toast.error('El pago no se pudo completar. Podés reintentar el cobro.');
    } else if (payment === 'pending') {
      toast('Tu pago quedó pendiente de acreditación.', { icon: '⏳' });
    } else if (payment === 'return') {
      toast('Verificando el estado de tu pago…', { icon: '🔄' });
    }
    // Solo al montar / cambiar el query param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('payment')]);

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [storePhone, setStorePhone] = useState(null);
  
  // Lógica de reintento para evitar pantalla en blanco en pedidos nuevos
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;
  const isRetryingNewOrder = isNewOrder && !order && retryCount > 0 && retryCount <= maxRetries;
  const retryProgress = Math.min(((retryCount + 1) / (maxRetries + 1)) * 100, 100);

  // Suscripcion realtime + poll de respaldo. Se setea UNA SOLA VEZ por trackingCode.
  // Antes este efecto tenia retryCount en deps y recreaba canal+intervalo en cada
  // reintento (5 subscribe/unsubscribe + duplicacion de toasts si llegaban eventos
  // durante un retry).
  // Polling del estado del pedido. Antes usaba realtime (postgres_changes), que
  // requeria que anon pudiera hacer SELECT directo sobre store_orders (fuga de
  // datos cross-tenant). Ahora el rastreo se hace via RPC SECURITY DEFINER
  // (track_store_order_by_code) y refrescamos por poll cada 30s.
  useEffect(() => {
    const intervalId = setInterval(() => {
        fetchOrder(false);
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingCode]);

  // Efecto separado para los reintentos del pedido recien creado.
  // No tiene canal ni interval, solo dispara fetchOrder cuando cambia retryCount.
  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingCode, retryCount]);

  const fetchOrder = async (showLoading = true) => {
    // Si es la primera carga, activamos el spinner
    if (showLoading && !order && retryCount === 0) setLoading(true); 

    try {
      // Rastreo via RPC SECURITY DEFINER (no expone store_orders a anon).
      // Fallback al SELECT directo si la RPC no existe todavia (pre-deploy).
      let data = null;
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('track_store_order_by_code', { p_tracking_code: trackingCode });

      if (rpcError) {
        const { data: legacy, error } = await supabase
          .from('store_orders')
          .select(`
              *,
              sale:sales!store_orders_sale_id_fkey (
                  total,
                  sale_items (
                      quantity,
                      product_name_snapshot,
                      unit_price
                  )
              )
          `)
          .eq('tracking_code', trackingCode)
          .maybeSingle();
        if (error) throw error;
        data = legacy;
      } else {
        data = rpcData || null;
      }

      // --- LÓGICA DE REINTENTO BLINDADA ---
      if (!data && isNewOrder && retryCount < maxRetries) {
          console.log(`Pedido no encontrado aún. Reintentando (${retryCount + 1}/${maxRetries})...`);
          setTimeout(() => {
              setRetryCount(prev => prev + 1);
          }, 2000);
          return; // Sale de la función limpiamente (el spinner sigue girando)
      }

      // --- SI LLEGAMOS ACÁ, ENCONTRÓ EL PEDIDO (O SE AGOTARON LOS REINTENTOS) ---
      setOrder(data);

      if (data && data.store_whatsapp) {
          setStorePhone(data.store_whatsapp);
      } else if (data && data.organization_id) {
          const { data: config } = await supabase
            .from('store_config')
            .select('whatsapp')
            .eq('organization_id', data.organization_id)
            .maybeSingle();
          if(config?.whatsapp) setStorePhone(config.whatsapp);
      }

      // Finalizó con éxito, quitamos el loading
      setLoading(false);

    } catch (err) {
      console.error("Error fetching order:", err);
      // Hubo un error de red o de DB, quitamos el loading para no trabar la pantalla
      setLoading(false);
    }
  };

  const stepsConfig = useMemo(() => {
      if (!order) return [];

      const isEnvio = order.delivery_type === 'envio';

      const steps = [
          { key: 'recibido', label: 'Recibido', icon: Clock, color: 'bg-blue-500' },
          { key: 'en_preparacion', label: 'En Preparación', icon: Package, color: 'bg-amber-500' },
      ];

      steps.push({ 
          key: 'listo', 
          label: isEnvio ? 'Listo para Envío' : 'Listo para Retirar', 
          icon: isEnvio ? Package : Store, 
          color: 'bg-purple-500' 
      });

      if (isEnvio) {
          steps.push({ key: 'en_camino', label: 'En Camino', icon: Truck, color: 'bg-indigo-500' });
      }

      steps.push({ 
          key: 'entregado', 
          label: isEnvio ? 'Entregado' : 'Retirado',
          icon: CheckCircle, 
          color: 'bg-green-500' 
      });

      return steps;
  }, [order?.delivery_type]);

  const getEstimatedTime = () => {
      if (!order) return null;
      const created = new Date(order.created_at);
      const eta = new Date(created.getTime() + 45 * 60000); 
      return eta.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  };

  const getWhatsAppLink = () => {
      if(!storePhone) return null;
      const cleanPhone = storePhone.replace(/\D/g,'');
      const phone = cleanPhone.startsWith('54') ? cleanPhone : `549${cleanPhone}`;
      const text = `Hola, consulto por el estado de mi pedido #${order.tracking_code}`;
      return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-full max-w-sm mx-auto px-5">
            <div className="bg-white border border-slate-200 shadow-sm rounded-[28px] p-6 text-center">
                <div className="w-14 h-14 rounded-3xl bg-slate-50 border border-slate-200 shadow-sm flex items-center justify-center mx-auto mb-4 animate-pulse">
                     <Package className="text-slate-300" size={22} />
                </div>
                <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">
                    {isNewOrder ? 'Confirmando pedido...' : 'Buscando pedido...'}
                </p>
                <h1 className="text-xl font-black text-slate-800 tracking-tight mt-3">
                    {isNewOrder ? 'Estamos registrando tu compra' : 'Cargando seguimiento'}
                </h1>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                    {isNewOrder
                        ? 'El pago ya fue enviado. Puede tardar unos segundos en reflejarse el pedido.'
                        : 'Estamos consultando el estado mas reciente de tu pedido.'}
                </p>

                {isNewOrder && (
                    <div className="mt-5 text-left">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Estado</span>
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                Intento {Math.min(retryCount + 1, maxRetries + 1)} de {maxRetries + 1}
                            </span>
                        </div>
                        <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-[#0B2A36] to-[#3FF2AA] transition-all duration-500"
                                style={{ width: `${retryProgress}%` }}
                            ></div>
                        </div>
                        <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                            {isRetryingNewOrder
                                ? 'Todavia no aparece en la primera consulta. Estamos reintentando automaticamente.'
                                : 'No cierres esta pantalla. Te vamos a mostrar el seguimiento apenas quede confirmado.'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    </div>
  );

  if (!order) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center animate-in fade-in zoom-in duration-300">
        <div className="w-16 h-16 bg-white border border-slate-200 rounded-3xl flex items-center justify-center shadow-sm mb-4">
            <Package size={32} className="text-slate-300" />
        </div>
        <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight">Pedido no encontrado</h1>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-6 mt-2 max-w-md">
            No pudimos encontrar el código <strong>{trackingCode}</strong>.
        </p>
        {isNewOrder && (
            <p className="text-sm text-slate-500 max-w-md mb-6 leading-relaxed">
                Si acabas de pagar, es posible que la confirmacion haya demorado mas de lo esperado. Puedes intentar abrir nuevamente el enlace en unos segundos.
            </p>
        )}
        <Link to="/" className="px-6 py-3 bg-[#0B2A36] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:shadow-lg transition-all">
            Ir al Inicio
        </Link>
    </div>
  );

  const currentStepIndex = stepsConfig.findIndex(s => s.key === order.status);
  const isCancelled = order.status === 'cancelado';
  const currentStepInfo = stepsConfig[currentStepIndex] || (isCancelled ? { label: 'Cancelado', color: 'bg-red-500' } : stepsConfig[0]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-24">
        
        {/* BANNER DE AGRADECIMIENTO (SOLO PEDIDOS NUEVOS) */}
        {isNewOrder && (
            <div className="bg-[#3FF2AA] text-[#0B2A36] text-center py-3 px-4 animate-in slide-in-from-top border-b border-[#32cf90]">
                <p className="text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2">
                    <CheckCircle size={14} /> ¡Gracias por tu compra! Tu pedido fue recibido.
                </p>
            </div>
        )}

        {/* HEADER ESTILO PRODUCTOS */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm/50 backdrop-blur-md bg-white/90">
            <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link to={order.store_slug ? `/tienda/${order.store_slug?.toLowerCase()}` : '/'} className="w-10 h-10 bg-slate-50 rounded-2xl flex items-center justify-center hover:bg-slate-100 transition-colors text-slate-400 border border-slate-100 shadow-sm">
                        <ChevronLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="font-black text-lg text-slate-800 leading-none uppercase tracking-tight">Rastreo</h1>
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Ref: {order.tracking_code}</p>
                    </div>
                </div>
                {isCancelled ? (
                     <div className="px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest text-white shadow-sm bg-red-500">
                        Cancelado
                    </div>
                ) : (
                    <div className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest text-white shadow-sm ${currentStepInfo.color}`}>
                        {currentStepInfo.label}
                    </div>
                )}
            </div>
        </div>

        <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
            
            {/* AVISOS PUSH DEL PEDIDO (también es la confirmación del checkout) */}
            <PushAvisoPedido
                trackingCode={order.tracking_code}
                status={order.status}
                whatsappLink={getWhatsAppLink()}
                destacado={isNewOrder}
            />

            {/* HORA Y TIEMPO ESTIMADO */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex justify-between items-center">
                 <div>
                     <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1">Hora Pedido</p>
                     <p className="text-xl font-black text-slate-800 tracking-tight">
                         {new Date(order.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs
                     </p>
                 </div>
                 {!isCancelled && order.status !== 'entregado' && (
                     <div className="text-right">
                         <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1">Llegada Estimada</p>
                         <p className="text-xl font-black text-[#0B2A36] tracking-tight">
                             ~ {getEstimatedTime()} hs
                         </p>
                     </div>
                 )}
            </div>

            {/* LÍNEA DE TIEMPO (STATUS) */}
            {!isCancelled ? (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 overflow-hidden">
                    <div className="relative flex justify-between items-center px-2">
                        {/* Barra de fondo */}
                        <div className="absolute top-1/2 left-0 w-full h-1 bg-slate-100 -z-0 rounded-full"></div>
                        {/* Barra de progreso animada */}
                        <div 
                            className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-1000 ease-out -z-0 rounded-full"
                            style={{ width: `${currentStepIndex >= 0 ? (currentStepIndex / (stepsConfig.length - 1)) * 100 : 0}%` }}
                        ></div>
                        
                        {stepsConfig.map((step, index) => {
                            const isActive = index <= currentStepIndex;
                            const isCurrent = index === currentStepIndex;
                            const Icon = step.icon;
                            return (
                                <div key={step.key} className="relative z-10 flex flex-col items-center gap-3" style={{ width: '40px' }}>
                                    <div className={`w-9 h-9 md:w-11 md:h-11 rounded-2xl flex items-center justify-center border-2 transition-all duration-500 shadow-sm ${isActive ? 'bg-emerald-500 border-emerald-500 text-white scale-110 shadow-emerald-200' : 'bg-white border-slate-100 text-slate-300'}`}>
                                        <Icon size={isCurrent ? 18 : 16} />
                                    </div>
                                    <span className={`text-[8px] md:text-[9px] font-black uppercase tracking-widest text-center absolute top-12 md:top-14 w-24 transition-all duration-300 ${isActive ? 'text-slate-800' : 'text-slate-300'} ${isCurrent ? 'opacity-100 scale-105' : 'opacity-80 md:opacity-100'}`}>
                                        {step.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="h-8 md:h-10"></div>
                </div>
            ) : (
                <div className="bg-red-50 border border-red-100 p-6 rounded-2xl flex flex-col items-center text-center shadow-sm animate-in zoom-in">
                    <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-3 text-red-500 shadow-inner"><AlertCircle size={28} /></div>
                    <h2 className="text-lg font-black text-red-800 uppercase tracking-tight mb-1">Pedido Cancelado</h2>
                    <p className="text-[10px] text-red-600 font-bold uppercase tracking-wide">Este pedido ha sido cancelado. Si tienes dudas, contáctanos.</p>
                </div>
            )}

            {/* BOTÓN WHATSAPP */}
            {storePhone && !isCancelled && order.status !== 'entregado' && (
                <a 
                    href={getWhatsAppLink()} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full bg-[#3FF2AA] text-[#0B2A36] py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-100 flex items-center justify-center gap-2 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                    <MessageCircle size={18} /> Consultar por WhatsApp
                </a>
            )}

            {/* DETALLES DE ENTREGA */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50/50 px-6 py-3 border-b border-slate-100 flex items-center gap-2">
                    <MapPin size={14} className="text-slate-400" />
                    <h3 className="text-[9px] font-black uppercase tracking-widest text-slate-500">Datos de Entrega</h3>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-[9px] text-slate-400 font-black uppercase mb-2 tracking-widest">Cliente</p>
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-blue-50 rounded-2xl border border-blue-100 flex items-center justify-center text-blue-600 shrink-0 shadow-sm">
                                <User size={18}/>
                            </div>
                            <div>
                                <p className="font-bold text-slate-800 text-sm uppercase">{order.customer_snapshot?.name || 'Cliente'}</p>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mt-0.5">{order.customer_snapshot?.contact}</p>
                                {order.delivery_type === 'retiro' && (
                                    <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[9px] font-black uppercase tracking-widest">Quien retira</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div>
                        <p className="text-[9px] text-slate-400 font-black uppercase mb-2 tracking-widest">Método</p>
                        <div className="mb-2">
                            {order.delivery_type === 'envio' ? (
                                <span className="inline-flex px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-xl text-[9px] font-black uppercase tracking-widest items-center gap-2 border border-indigo-100">
                                    <Truck size={12} /> Envío a Domicilio
                                </span>
                            ) : (
                                <span className="inline-flex px-3 py-1.5 bg-orange-50 text-orange-700 rounded-xl text-[9px] font-black uppercase tracking-widest items-center gap-2 border border-orange-100">
                                    <Store size={12} /> Retiro en Local
                                </span>
                            )}
                        </div>
                        {order.delivery_type === 'envio' && order.shipping_address && (
                            <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100">
                                <p className="font-bold text-slate-800">{order.shipping_address.address}</p>
                                <p className="text-[10px] text-slate-500 uppercase font-bold mt-0.5 tracking-wide">{order.shipping_address.city}</p>
                                {order.shipping_address.notes && (
                                    <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] italic text-slate-500 font-medium">
                                        " {order.shipping_address.notes} "
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* RESUMEN DEL PEDIDO */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50/50 px-6 py-3 border-b border-slate-100 flex items-center gap-2">
                    <ShoppingBag size={14} className="text-slate-400" />
                    <h3 className="text-[9px] font-black uppercase tracking-widest text-slate-500">Resumen de Compra</h3>
                </div>
                <div className="divide-y divide-slate-50">
                    {order.sale?.sale_items?.map((item, idx) => (
                        <div key={idx} className="p-4 flex justify-between items-center hover:bg-slate-50/50 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center text-[10px] font-black text-slate-500 border border-slate-200">
                                    {item.quantity}x
                                </div>
                                <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">{getSaleItemDisplayName(item)}</span>
                            </div>
                            <span className="text-sm font-black text-slate-900">
                                $ {Number(item.unit_price * item.quantity).toLocaleString('es-AR')}
                            </span>
                        </div>
                    ))}
                </div>
                <div className="p-6 bg-slate-50/50 border-t border-slate-100 space-y-2">
                    {Number(order.shipping_cost) > 0 && (
                        <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            <span>Costo de Envío</span>
                            <span>$ {Number(order.shipping_cost).toLocaleString('es-AR')}</span>
                        </div>
                    )}
                    <div className="flex justify-between items-end pt-3 border-t border-slate-200">
                        <span className="font-black text-slate-900 uppercase text-[10px] tracking-widest">Total Pagado</span>
                        <span className="text-2xl font-black text-slate-900 tracking-tight">$ {Number(order.sale?.total || 0).toLocaleString('es-AR')}</span>
                    </div>
                </div>
            </div>

            <div className="text-center pt-6 pb-6">
                <Link to={order.store_slug ? `/tienda/${order.store_slug?.toLowerCase()}` : '/'} className="inline-flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-slate-800 transition-colors bg-white px-5 py-2.5 rounded-2xl shadow-sm hover:shadow-md border border-slate-200">
                    <ChevronLeft size={14} /> Volver a la Tienda
                </Link>
            </div>
        </main>
    </div>
  );
}
